/**
 * Extreme Events Configuration
 * Comprehensive list of 101 extreme risk events for Solar + BESS projects
 * Based on power law distribution analysis and Philippines market context
 */

/**
 * Risk Categories with Power Law Parameters
 */
export const RISK_CATEGORIES = {
    FINANCIAL_RISK: {
        id: 'FINANCIAL_RISK',
        name: 'Financial Risk Events',
        description: 'Market-driven financial risks affecting project economics',
        color: '#dc2626', // red-600
        icon: '🏦',
        powerLawParams: { k: 0.15, alpha: 2.2 },
        enabled: true
    },
    STRATEGIC_RISK: {
        id: 'STRATEGIC_RISK',
        name: 'Strategic Risk Events',
        description: 'Business strategy and competitive risks',
        color: '#7c3aed', // violet-600
        icon: '📈',
        powerLawParams: { k: 0.12, alpha: 2.4 },
        enabled: true
    },
    HAZARD_RISK: {
        id: 'HAZARD_RISK',
        name: 'Hazard Risk Events',
        description: 'Natural disasters and physical threats',
        color: '#ea580c', // orange-600
        icon: '⚡',
        powerLawParams: { k: 0.20, alpha: 1.8 },
        enabled: true
    },
    OPERATIONS_RISK: {
        id: 'OPERATIONS_RISK',
        name: 'Operations Risk Events',
        description: 'Operational and technical system failures',
        color: '#059669', // emerald-600
        icon: '⚙️',
        powerLawParams: { k: 0.18, alpha: 2.1 },
        enabled: true
    },
    EXTREME_BLACK_SWAN: {
        id: 'EXTREME_BLACK_SWAN',
        name: 'Extreme Black Swan Events',
        description: 'Ultra-rare catastrophic events with massive impact',
        color: '#1f2937', // gray-800
        icon: '🌋',
        powerLawParams: { k: 0.05, alpha: 3.0 },
        enabled: false // Disabled by default due to extreme rarity
    }
};

/**
 * Complete list of 101 extreme risk events
 */
export const EXTREME_EVENTS = {
    // FINANCIAL RISK EVENTS (19 events)
    FINANCIAL_RISK: [
        {
            id: 'accounting_tax_changes',
            name: 'Accounting/Tax Law Changes',
            probability: 0.02, // 1:50 years
            impactRange: [0.3, 0.8],
            description: 'Retroactive tax policy changes, removal of renewable incentives',
            philippinesContext: 'BIR policy changes, DOE incentive restructuring',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'adverse_environmental_regulations',
            name: 'Adverse Environmental Regulations',
            probability: 0.04, // 1:25 years
            impactRange: [0.4, 0.9],
            description: 'Sudden EIA requirement changes, protected area reclassification',
            philippinesContext: 'DENR policy shifts, NIPAS Act enforcement',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'currency_inconvertibility',
            name: 'Currency Inconvertibility',
            probability: 0.005, // 1:200 years
            impactRange: [0.1, 0.5],
            description: 'PHP devaluation crisis, capital controls implementation',
            philippinesContext: 'BSP emergency measures, foreign exchange restrictions',
            enabled: true,
            severity: 'catastrophic'
        },
        {
            id: 'economy_recession',
            name: 'Economy Recession',
            probability: 0.067, // 1:15 years
            impactRange: [0.6, 0.9],
            description: 'Asian financial crisis repeat, severe GDP contraction',
            philippinesContext: 'NEDA economic projections, OFW remittance decline',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'financial_markets_instability',
            name: 'Financial Markets Instability',
            probability: 0.04, // 1:25 years
            impactRange: [0.5, 0.8],
            description: 'Banking sector collapse, credit market freeze',
            philippinesContext: 'BSP intervention required, PSE volatility',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'interest_rate_shock',
            name: 'Interest Rate Shock',
            probability: 0.029, // 1:35 years
            impactRange: [0.7, 0.9],
            description: 'Central bank emergency rate hikes (+10% overnight)',
            philippinesContext: 'BSP policy rate emergency adjustment',
            enabled: true,
            severity: 'moderate'
        },
        {
            id: 'credit_default',
            name: 'Credit Default',
            probability: 0.0067, // 1:150 years
            impactRange: [0.0, 0.3],
            description: 'Project finance lender bankruptcy, debt acceleration',
            philippinesContext: 'Major bank failure, international lender withdrawal',
            enabled: true,
            severity: 'catastrophic'
        },
        {
            id: 'fuel_price_shock',
            name: 'Fuel Price Shock',
            probability: 0.05, // 1:20 years
            impactRange: [1.2, 2.0],
            description: 'Oil crisis affecting grid electricity pricing (positive impact)',
            philippinesContext: 'Malampaya depletion, coal import restrictions',
            enabled: true,
            severity: 'beneficial'
        },
        {
            id: 'revenue_management_failure',
            name: 'Revenue Management Failure',
            probability: 0.02, // 1:50 years
            impactRange: [0.2, 0.6],
            description: 'Grid operator insolvency, PPA counterparty default',
            philippinesContext: 'NGCP financial distress, utility bankruptcy',
            enabled: true,
            severity: 'severe'
        },
        // Additional Financial Risk Events (10 more)
        {
            id: 'adverse_industry_regulations',
            name: 'Adverse Changes in Industry Regulations',
            probability: 0.03,
            impactRange: [0.6, 0.9],
            description: 'Policy shifts affecting renewable energy sector',
            philippinesContext: 'DOE renewable energy policy reversal',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'acquisitions_divestitures',
            name: 'Equipment/Facilities/Business Acquisitions & Divestitures',
            probability: 0.025,
            impactRange: [0.7, 0.9],
            description: 'Strategic restructuring impacts',
            philippinesContext: 'Forced asset sales, ownership changes',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'asset_valuation',
            name: 'Asset Valuation',
            probability: 0.04,
            impactRange: [0.8, 0.95],
            description: 'Mark-to-market losses, impairment charges',
            philippinesContext: 'SEC valuation requirements, auditor concerns',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'currency_fx_fluctuations',
            name: 'Currency & Foreign Exchange Rate Fluctuations',
            probability: 0.1,
            impactRange: [0.85, 0.95],
            description: 'USD/PHP volatility affecting costs',
            philippinesContext: 'BSP intervention, import cost volatility',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'debt_credit_rating',
            name: 'Debt & Credit Rating',
            probability: 0.02,
            impactRange: [0.7, 0.9],
            description: 'Rating downgrades increasing borrowing costs',
            philippinesContext: 'Philippine sovereign rating impact',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'healthcare_pension_cost',
            name: 'Health Care & Pension Cost',
            probability: 0.05,
            impactRange: [0.9, 0.98],
            description: 'Employee benefit cost escalation',
            philippinesContext: 'PhilHealth premium increases, SSS changes',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'financial_controls_reporting',
            name: 'Inadequate/Inaccurate Financial Controls & Reporting',
            probability: 0.02,
            impactRange: [0.6, 0.8],
            description: 'Internal control failures',
            philippinesContext: 'SEC compliance failures, audit findings',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'liquidity_cash',
            name: 'Liquidity/Cash',
            probability: 0.03,
            impactRange: [0.7, 0.9],
            description: 'Working capital shortages, cash flow mismatches',
            philippinesContext: 'FIT payment delays, grid operator arrears',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'shareholder_activism',
            name: 'Shareholder Activism',
            probability: 0.01,
            impactRange: [0.8, 0.95],
            description: 'Investor pressure affecting operations',
            philippinesContext: 'Corporate governance requirements, proxy battles',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'uncompetitive_cost_structure',
            name: 'Uncompetitive Cost Structure',
            probability: 0.04,
            impactRange: [0.6, 0.8],
            description: 'LCOE above market rates',
            philippinesContext: 'RCOA competitive pricing, new technology costs',
            enabled: false,
            severity: 'moderate'
        }
    ],

    // STRATEGIC RISK EVENTS (23 events)
    STRATEGIC_RISK: [
        {
            id: 'technology_obsolescence',
            name: 'Technology Obsolescence',
            probability: 0.04, // 1:25 years
            impactRange: [0.4, 0.7],
            description: 'Breakthrough solar/storage tech makes project uncompetitive',
            philippinesContext: 'Perovskite cells, solid-state batteries',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'market_share_battles',
            name: 'Market Share Battles',
            probability: 0.083, // 1:12 years
            impactRange: [0.7, 0.9],
            description: 'Utility-scale solar oversupply, aggressive competitor pricing',
            philippinesContext: 'Green Energy Auction Program competition',
            enabled: true,
            severity: 'moderate'
        },
        {
            id: 'foreign_market_protectionism',
            name: 'Foreign Market Protectionism',
            probability: 0.033, // 1:30 years
            impactRange: [0.5, 0.8],
            description: 'Chinese solar equipment import restrictions, supply chain disruption',
            philippinesContext: 'Trade war impacts, domestic content requirements',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'new_technology_disruption',
            name: 'New Technology Disruption',
            probability: 0.02, // 1:50 years
            impactRange: [0.3, 0.6],
            description: 'Fusion power breakthrough, hydrogen economy transition',
            philippinesContext: 'Breakthrough energy technologies, grid transformation',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'ethics_violations',
            name: 'Ethics Violations',
            probability: 0.0125, // 1:80 years
            impactRange: [0.1, 0.4],
            description: 'Corruption scandal, environmental damage cover-up',
            philippinesContext: 'Anti-corruption investigations, environmental violations',
            enabled: true,
            severity: 'catastrophic'
        },
        {
            id: 'joint_venture_failure',
            name: 'Joint Venture/Alliance Failure',
            probability: 0.056, // 1:18 years
            impactRange: [0.6, 0.8],
            description: 'EPC contractor bankruptcy, O&M provider breach',
            philippinesContext: 'Local partner issues, foreign JV dissolution',
            enabled: true,
            severity: 'moderate'
        },
        {
            id: 'intellectual_property_loss',
            name: 'Loss of Intellectual Property',
            probability: 0.01, // 1:100 years
            impactRange: [0.8, 0.9],
            description: 'Patent infringement lawsuit, technology theft',
            philippinesContext: 'IP tribunal cases, technology transfer disputes',
            enabled: true,
            severity: 'minor'
        },
        {
            id: 'public_boycott',
            name: 'Public Boycott',
            probability: 0.017, // 1:60 years
            impactRange: [0.4, 0.7],
            description: 'Community opposition, environmental activism',
            philippinesContext: 'Indigenous peoples rights, local government opposition',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'price_wars',
            name: 'Price Wars',
            probability: 0.067, // 1:15 years
            impactRange: [0.6, 0.8],
            description: 'Grid parity disruption, merchant power market collapse',
            philippinesContext: 'WESM price volatility, oversupply conditions',
            enabled: true,
            severity: 'moderate'
        },
        // Additional Strategic Risk Events (14 more)
        {
            id: 'brand_loyalty_attacks',
            name: 'Attacks on Brand Loyalty',
            probability: 0.02,
            impactRange: [0.8, 0.95],
            description: 'Reputation damage affecting market position',
            philippinesContext: 'Social media campaigns, competitor attacks',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'corporate_culture',
            name: 'Corporate Culture',
            probability: 0.03,
            impactRange: [0.85, 0.95],
            description: 'Internal cultural conflicts affecting performance',
            philippinesContext: 'Management-labor disputes, cultural misalignment',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'customer_relations',
            name: 'Customer Relations',
            probability: 0.04,
            impactRange: [0.7, 0.9],
            description: 'Grid operator/PPA counterparty relationship deterioration',
            philippinesContext: 'NGCP relationship issues, utility disputes',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'dealer_supplier_relations',
            name: 'Dealer/Supplier Relations',
            probability: 0.05,
            impactRange: [0.8, 0.95],
            description: 'EPC contractor relationship breakdown',
            philippinesContext: 'Local contractor issues, supply chain conflicts',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'demand_seasonality',
            name: 'Demand Seasonality & Variability',
            probability: 0.06,
            impactRange: [0.9, 0.98],
            description: 'Grid demand pattern changes',
            philippinesContext: 'Economic activity shifts, demand response programs',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'gotta_have_products',
            name: '"Gotta-have" Products',
            probability: 0.02,
            impactRange: [0.7, 0.9],
            description: 'Technology lock-in forcing expensive upgrades',
            philippinesContext: 'Grid code compliance, mandatory technology updates',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'management_oversight',
            name: 'Inadequate Management Oversight',
            probability: 0.025,
            impactRange: [0.8, 0.95],
            description: 'Poor governance and decision-making',
            philippinesContext: 'Board oversight failures, executive incompetence',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'ineffective_planning',
            name: 'Ineffective Planning',
            probability: 0.03,
            impactRange: [0.8, 0.95],
            description: 'Strategic planning failures',
            philippinesContext: 'Market timing errors, capacity planning mistakes',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'ma_consolidation',
            name: 'M&A Industry Consolidation',
            probability: 0.02,
            impactRange: [0.7, 0.9],
            description: 'Market consolidation affecting competitiveness',
            philippinesContext: 'Foreign acquisitions, local market concentration',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'negative_media_coverage',
            name: 'Negative Media Coverage',
            probability: 0.04,
            impactRange: [0.85, 0.95],
            description: 'Public relations crises',
            philippinesContext: 'Environmental concerns coverage, corruption allegations',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'new_foreign_competitors',
            name: 'New or Foreign Competitors',
            probability: 0.05,
            impactRange: [0.8, 0.9],
            description: 'Market entry by stronger competitors',
            philippinesContext: 'Chinese developers, international utilities',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'offensive_advertising',
            name: 'Offensive Advertising',
            probability: 0.02,
            impactRange: [0.9, 0.98],
            description: 'Competitive attacks on renewable energy',
            philippinesContext: 'Fossil fuel industry campaigns, misinformation',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'perceived_quality',
            name: 'Perceived Quality',
            probability: 0.03,
            impactRange: [0.8, 0.95],
            description: 'Technology performance reputation issues',
            philippinesContext: 'Equipment quality concerns, performance guarantees',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'product_design_engineering',
            name: 'Product Design & Engineering',
            probability: 0.02,
            impactRange: [0.7, 0.9],
            description: 'Technical design flaws',
            philippinesContext: 'Engineering errors, inadequate specifications',
            enabled: false,
            severity: 'moderate'
        }
    ],

    // HAZARD RISK EVENTS (35 events)
    HAZARD_RISK: [
        {
            id: 'super_typhoon',
            name: 'Super Typhoon (Category 5)',
            probability: 0.04, // 1:25 years
            impactRange: [0.2, 0.6],
            description: 'Yolanda-level destruction, total facility loss',
            philippinesContext: 'Climate change intensification, stronger storms',
            enabled: true,
            severity: 'catastrophic'
        },
        {
            id: 'major_earthquake',
            name: 'Major Earthquake (≥7.0)',
            probability: 0.02, // 1:50 years
            impactRange: [0.1, 0.4],
            description: 'Big One Manila fault rupture, infrastructure collapse',
            philippinesContext: 'West Valley Fault, Marikina Fault system',
            enabled: true,
            severity: 'catastrophic'
        },
        {
            id: 'volcanic_eruption',
            name: 'Volcanic Eruption',
            probability: 0.0067, // 1:150 years
            impactRange: [0.0, 0.3],
            description: 'Pinatubo-level ashfall, long-term solar panel damage',
            philippinesContext: 'Taal, Mayon, Kanlaon activity',
            enabled: true,
            severity: 'catastrophic'
        },
        {
            id: 'tsunami',
            name: 'Tsunami',
            probability: 0.005, // 1:200 years
            impactRange: [0.0, 0.2],
            description: 'Coastal facility complete destruction',
            philippinesContext: 'Pacific Ring of Fire, offshore earthquakes',
            enabled: true,
            severity: 'catastrophic'
        },
        {
            id: 'extended_flooding',
            name: 'Extended Flooding',
            probability: 0.033, // 1:30 years
            impactRange: [0.3, 0.7],
            description: 'Monsoon season extremes, infrastructure damage',
            philippinesContext: 'Climate change enhanced monsoons, sea level rise',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'wildfire',
            name: 'Wildfire',
            probability: 0.02, // 1:50 years
            impactRange: [0.2, 0.5],
            description: 'Dry season grass fires, transmission line damage',
            philippinesContext: 'El Niño drought conditions, agricultural burning',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'terrorism_sabotage',
            name: 'Terrorism/Sabotage',
            probability: 0.01, // 1:100 years
            impactRange: [0.1, 0.6],
            description: 'Infrastructure attack, political violence',
            philippinesContext: 'NPA activities, Abu Sayyaf, MILF conflicts',
            enabled: true,
            severity: 'catastrophic'
        },
        {
            id: 'pandemic_lockdown',
            name: 'Pandemic Lockdown',
            probability: 0.025, // 1:40 years
            impactRange: [0.7, 0.9],
            description: 'COVID-level restrictions, construction delays',
            philippinesContext: 'Government quarantine measures, travel restrictions',
            enabled: true,
            severity: 'moderate'
        },
        {
            id: 'geopolitical_conflict',
            name: 'Geopolitical Conflict',
            probability: 0.013, // 1:75 years
            impactRange: [0.2, 0.8],
            description: 'South China Sea tensions, supply chain disruption',
            philippinesContext: 'China territorial disputes, US-China trade war',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'disease_epidemic',
            name: 'Disease/Epidemic',
            probability: 0.029, // 1:35 years
            impactRange: [0.8, 0.9],
            description: 'Workforce disruption, operational constraints',
            philippinesContext: 'Dengue outbreaks, emerging infectious diseases',
            enabled: true,
            severity: 'minor'
        },
        // Additional Hazard Risk Events (25 more)
        {
            id: 'third_party_liability',
            name: '3rd Party Liability',
            probability: 0.05,
            impactRange: [0.9, 0.98],
            description: 'Third-party property damage claims',
            philippinesContext: 'Community damage claims, agricultural losses',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'animal_insect_infestation',
            name: 'Animal/Insect Infestation',
            probability: 0.1,
            impactRange: [0.95, 0.99],
            description: 'Pest damage to equipment and infrastructure',
            philippinesContext: 'Termites, rodents, tropical insects',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'building_equipment_fire',
            name: 'Building or Equipment Fire',
            probability: 0.02,
            impactRange: [0.6, 0.8],
            description: 'Electrical fires, control room fires',
            philippinesContext: 'Hot climate equipment stress, electrical faults',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'blizzard_ice_storms',
            name: 'Blizzard/Ice Storms',
            probability: 0.001,
            impactRange: [0.9, 0.99],
            description: 'Unusual cold weather events (rare in Philippines)',
            philippinesContext: 'Climate anomalies, freak weather',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'boiler_machinery_explosion',
            name: 'Boiler or Machinery Explosion',
            probability: 0.005,
            impactRange: [0.4, 0.7],
            description: 'High-pressure equipment failure',
            philippinesContext: 'Transformer explosions, inverter failures',
            enabled: false,
            severity: 'severe'
        },
        {
            id: 'cargo_losses',
            name: 'Cargo Losses',
            probability: 0.02,
            impactRange: [0.8, 0.95],
            description: 'Equipment shipment losses during transportation',
            philippinesContext: 'Port congestion, shipping delays, theft',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'deductible_limits',
            name: 'Deductible Limits',
            probability: 0.03,
            impactRange: [0.9, 0.98],
            description: 'Insurance coverage gaps',
            philippinesContext: 'High deductibles, coverage exclusions',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'directors_liability',
            name: 'Directors Liability',
            probability: 0.01,
            impactRange: [0.85, 0.95],
            description: 'Management liability issues',
            philippinesContext: 'SEC enforcement, shareholder lawsuits',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'general_liability',
            name: 'General Liability',
            probability: 0.04,
            impactRange: [0.9, 0.98],
            description: 'Public liability claims',
            philippinesContext: 'Community relations, public safety',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'hail_damage',
            name: 'Hail Damage',
            probability: 0.01,
            impactRange: [0.7, 0.9],
            description: 'Severe hailstorm panel damage',
            philippinesContext: 'Supercell thunderstorms, climate extremes',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'hazardous_chemical_exposure',
            name: 'Hazardous Chemical Exposure',
            probability: 0.005,
            impactRange: [0.8, 0.95],
            description: 'Battery electrolyte, cleaning chemical risks',
            philippinesContext: 'BESS chemical hazards, maintenance chemicals',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'heavy_rain_thunderstorms',
            name: 'Heavy Rain/Thunderstorms',
            probability: 0.2,
            impactRange: [0.95, 0.99],
            description: 'Severe weather infrastructure damage',
            philippinesContext: 'Monsoon intensity, flash flooding',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'pollution_liability',
            name: 'Land/Water/Atmospheric Pollution',
            probability: 0.005,
            impactRange: [0.6, 0.8],
            description: 'Environmental contamination liability',
            philippinesContext: 'DENR violations, ECC compliance',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'lightning_strikes',
            name: 'Lightning Strikes',
            probability: 0.1,
            impactRange: [0.9, 0.98],
            description: 'Direct lightning equipment damage',
            philippinesContext: 'High lightning frequency, tropical storms',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'key_facility_loss',
            name: 'Loss of Key Facility',
            probability: 0.002,
            impactRange: [0.1, 0.5],
            description: 'Critical infrastructure destruction',
            philippinesContext: 'Substation damage, control building loss',
            enabled: false,
            severity: 'catastrophic'
        },
        {
            id: 'mold_exposure',
            name: 'Mold Exposure',
            probability: 0.02,
            impactRange: [0.95, 0.99],
            description: 'Facility health and safety issues',
            philippinesContext: 'High humidity conditions, building maintenance',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'product_liability',
            name: 'Product Liability',
            probability: 0.01,
            impactRange: [0.8, 0.95],
            description: 'Equipment defect liability claims',
            philippinesContext: 'Panel defects, inverter failures',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'property_damage',
            name: 'Property Damage',
            probability: 0.05,
            impactRange: [0.8, 0.95],
            description: 'Physical asset damage from various causes',
            philippinesContext: 'Equipment damage, infrastructure deterioration',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'severe_hot_cold_weather',
            name: 'Severe Hot/Cold Weather',
            probability: 0.05,
            impactRange: [0.9, 0.98],
            description: 'Extreme temperature equipment stress',
            philippinesContext: 'Heat waves, unusual temperature extremes',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'tornadoes',
            name: 'Tornadoes',
            probability: 0.001,
            impactRange: [0.3, 0.7],
            description: 'Rare but possible severe wind events',
            philippinesContext: 'Supercell development, waterspouts',
            enabled: false,
            severity: 'severe'
        },
        {
            id: 'wind_damage',
            name: 'Wind Damage',
            probability: 0.08,
            impactRange: [0.8, 0.95],
            description: 'High wind equipment and structure damage',
            philippinesContext: 'Monsoon winds, tropical storms',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'workers_compensation',
            name: 'Workers Compensation',
            probability: 0.1,
            impactRange: [0.95, 0.99],
            description: 'Employee injury and safety claims',
            philippinesContext: 'DOLE compliance, safety incidents',
            enabled: false,
            severity: 'minor'
        }
    ],

    // OPERATIONS RISK EVENTS (24 events)
    OPERATIONS_RISK: [
        {
            id: 'cyberattack_grid_systems',
            name: 'Cyberattack on Grid Systems',
            probability: 0.04, // 1:25 years
            impactRange: [0.4, 0.8],
            description: 'SCADA system hack, grid instability',
            philippinesContext: 'NGCP cybersecurity, critical infrastructure protection',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'loss_key_personnel',
            name: 'Loss of Key Personnel',
            probability: 0.083, // 1:12 years
            impactRange: [0.8, 0.9],
            description: 'Plant manager departure, technical expertise loss',
            philippinesContext: 'Brain drain, overseas migration',
            enabled: true,
            severity: 'minor'
        },
        {
            id: 'major_equipment_failure',
            name: 'Major Equipment Failure',
            probability: 0.05, // 1:20 years
            impactRange: [0.6, 0.8],
            description: 'Inverter cascade failure, transformer explosion',
            philippinesContext: 'Tropical climate stress, maintenance challenges',
            enabled: true,
            severity: 'moderate'
        },
        {
            id: 'supply_chain_collapse',
            name: 'Supply Chain Collapse',
            probability: 0.033, // 1:30 years
            impactRange: [0.5, 0.7],
            description: 'Chinese manufacturing disruption, shipping crisis',
            philippinesContext: 'Import dependencies, logistics challenges',
            enabled: true,
            severity: 'severe'
        },
        {
            id: 'it_system_failure',
            name: 'IT System Complete Failure',
            probability: 0.025, // 1:40 years
            impactRange: [0.7, 0.9],
            description: 'SCADA/monitoring system loss, operational blindness',
            philippinesContext: 'Technology infrastructure limitations',
            enabled: true,
            severity: 'moderate'
        },
        {
            id: 'access_route_destruction',
            name: 'Access Route Destruction',
            probability: 0.02, // 1:50 years
            impactRange: [0.6, 0.8],
            description: 'Bridge collapse, road infrastructure failure',
            philippinesContext: 'Remote location access, infrastructure quality',
            enabled: true,
            severity: 'moderate'
        },
        {
            id: 'utility_grid_instability',
            name: 'Utility Grid Instability',
            probability: 0.1, // 1:10 years
            impactRange: [0.8, 0.9],
            description: 'Rotating blackouts, frequency regulation issues',
            philippinesContext: 'NGCP grid challenges, Luzon-Visayas-Mindanao issues',
            enabled: true,
            severity: 'minor'
        },
        {
            id: 'theft_vandalism',
            name: 'Theft/Vandalism',
            probability: 0.125, // 1:8 years
            impactRange: [0.9, 0.95],
            description: 'Cable theft, panel damage, security breach',
            philippinesContext: 'Remote site security, copper theft',
            enabled: true,
            severity: 'minor'
        },
        {
            id: 'warranty_recall_campaign',
            name: 'Warranty Recall Campaign',
            probability: 0.017, // 1:60 years
            impactRange: [0.7, 0.9],
            description: 'Massive panel defect, inverter safety recall',
            philippinesContext: 'Equipment quality issues, manufacturer defects',
            enabled: true,
            severity: 'moderate'
        },
        {
            id: 'embezzlement_fraud',
            name: 'Embezzlement/Fraud',
            probability: 0.014, // 1:70 years
            impactRange: [0.6, 0.8],
            description: 'Financial controls failure, management fraud',
            philippinesContext: 'Internal controls, corporate governance',
            enabled: true,
            severity: 'moderate'
        },
        // Additional Operations Risk Events (14 more)
        {
            id: 'third_party_logistics_failures',
            name: '3PL Failures',
            probability: 0.03,
            impactRange: [0.9, 0.98],
            description: 'Third-party logistics provider failures',
            philippinesContext: 'Shipping delays, port congestion',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'customer_demand_reliability',
            name: 'Customer Demand Reliability',
            probability: 0.05,
            impactRange: [0.9, 0.98],
            description: 'Grid demand variability issues',
            philippinesContext: 'Economic activity changes, demand forecasting',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'dealer_distribution_failures',
            name: 'Dealer Distribution Network Failures',
            probability: 0.02,
            impactRange: [0.8, 0.95],
            description: 'Supply chain partner failures',
            philippinesContext: 'Local distributor issues, parts availability',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'extortion',
            name: 'Extortion',
            probability: 0.005,
            impactRange: [0.7, 0.9],
            description: 'Criminal threats against operations',
            philippinesContext: 'Security threats, protection rackets',
            enabled: false,
            severity: 'moderate'
        },
        {
            id: 'government_inquiries',
            name: 'Government Inquiries',
            probability: 0.02,
            impactRange: [0.9, 0.98],
            description: 'Regulatory investigations and audits',
            philippinesContext: 'Congressional inquiries, agency investigations',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'harassment_discrimination',
            name: 'Harassment & Discrimination',
            probability: 0.01,
            impactRange: [0.9, 0.98],
            description: 'Workplace legal issues',
            philippinesContext: 'DOLE complaints, workplace violations',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'health_safety_violations',
            name: 'Health & Safety Violations',
            probability: 0.03,
            impactRange: [0.85, 0.95],
            description: 'OSHA-equivalent compliance failures',
            philippinesContext: 'DOLE safety violations, workplace accidents',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'skill_shortage',
            name: 'HR: Skill Shortage',
            probability: 0.05,
            impactRange: [0.9, 0.98],
            description: 'Critical skill gaps in workforce',
            philippinesContext: 'Technical skill shortages, training needs',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'logistics_route_disruptions',
            name: 'Logistics Route Disruptions',
            probability: 0.04,
            impactRange: [0.9, 0.98],
            description: 'Transportation network failures',
            philippinesContext: 'Inter-island shipping, road conditions',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'loss_key_supplier',
            name: 'Loss of Key Supplier',
            probability: 0.02,
            impactRange: [0.8, 0.95],
            description: 'Critical vendor bankruptcy or withdrawal',
            philippinesContext: 'Equipment supplier changes, parts availability',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'operator_errors',
            name: 'Operator Errors/Accidental Damage',
            probability: 0.08,
            impactRange: [0.9, 0.98],
            description: 'Human error causing equipment damage',
            philippinesContext: 'Training issues, procedural failures',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'access_egress_restrictions',
            name: 'Restriction of Access/Egress',
            probability: 0.02,
            impactRange: [0.85, 0.95],
            description: 'Site access limitations',
            philippinesContext: 'Community conflicts, security issues',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'service_provider_failures',
            name: 'Service Provider Failures',
            probability: 0.03,
            impactRange: [0.8, 0.95],
            description: 'O&M contractor performance issues',
            philippinesContext: 'Local service provider quality, contract issues',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'supplier_cascade_problems',
            name: 'Tier 1,2,3...n Supplier Problems',
            probability: 0.025,
            impactRange: [0.85, 0.95],
            description: 'Supply chain cascading failures',
            philippinesContext: 'Multi-tier supply dependencies',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'turnover',
            name: 'Turnover',
            probability: 0.1,
            impactRange: [0.95, 0.99],
            description: 'High employee turnover disrupting operations',
            philippinesContext: 'OFW migration, competitive job market',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'utilities_failures',
            name: 'Utilities Failures: Communications/Electricity/Water/Power',
            probability: 0.06,
            impactRange: [0.9, 0.98],
            description: 'Infrastructure support failures',
            philippinesContext: 'Power outages, water supply, telecommunications',
            enabled: false,
            severity: 'minor'
        },
        {
            id: 'workplace_violence',
            name: 'Workplace Violence',
            probability: 0.005,
            impactRange: [0.8, 0.95],
            description: 'Security and safety incidents',
            philippinesContext: 'Security threats, workplace conflicts',
            enabled: false,
            severity: 'minor'
        }
    ],

    // EXTREME BLACK SWAN EVENTS (10 events)
    EXTREME_BLACK_SWAN: [
        {
            id: 'complete_grid_collapse',
            name: 'Complete Grid Collapse',
            probability: 0.005, // 1:200 years
            impactRange: [0.0, 0.2],
            description: 'National power grid failure, extended blackout',
            philippinesContext: 'Total NGCP system failure, nationwide blackout',
            enabled: false,
            severity: 'catastrophic'
        },
        {
            id: 'solar_technology_obsolescence',
            name: 'Solar Technology Obsolescence',
            probability: 0.0033, // 1:300 years
            impactRange: [0.0, 0.1],
            description: 'Revolutionary energy breakthrough makes solar obsolete',
            philippinesContext: 'Fusion breakthrough, revolutionary technology',
            enabled: false,
            severity: 'catastrophic'
        },
        {
            id: 'asteroid_impact',
            name: 'Asteroid Impact',
            probability: 0.002, // 1:500 years
            impactRange: [0.0, 0.0],
            description: 'Regional asteroid strike, total infrastructure destruction',
            philippinesContext: 'Tunguska-level impact, regional devastation',
            enabled: false,
            severity: 'catastrophic'
        },
        {
            id: 'supervolcano_eruption',
            name: 'Supervolcano Eruption',
            probability: 0.0025, // 1:400 years
            impactRange: [0.0, 0.1],
            description: 'Taal/Mayon massive eruption, years of ash coverage',
            philippinesContext: 'VEI 6+ eruption, ash blanket coverage',
            enabled: false,
            severity: 'catastrophic'
        },
        {
            id: 'nuclear_accident',
            name: 'Nuclear Accident',
            probability: 0.004, // 1:250 years
            impactRange: [0.1, 0.3],
            description: 'Regional nuclear facility accident, evacuation zones',
            philippinesContext: 'Bataan Nuclear Power Plant activation risks',
            enabled: false,
            severity: 'catastrophic'
        },
        {
            id: 'financial_system_collapse',
            name: 'Complete Financial System Collapse',
            probability: 0.0033, // 1:300 years
            impactRange: [0.0, 0.2],
            description: 'Banking system failure, currency collapse',
            philippinesContext: 'Peso collapse, banking system failure',
            enabled: false,
            severity: 'catastrophic'
        },
        {
            id: 'ai_singularity_disruption',
            name: 'AI Singularity Disruption',
            probability: 0.002, // 1:500 years
            impactRange: [0.0, 10.0],
            description: 'Artificial general intelligence transforms energy sector',
            philippinesContext: 'Technological singularity, AI transformation',
            enabled: false,
            severity: 'transformational'
        },
        {
            id: 'alien_contact_invasion',
            name: 'Alien Contact/Invasion',
            probability: 0.002, // 1:500 years
            impactRange: [0.0, 5.0],
            description: 'Extraterrestrial contact disrupts civilization',
            philippinesContext: 'First contact scenario, technological disruption',
            enabled: false,
            severity: 'transformational'
        },
        {
            id: 'climate_tipping_point',
            name: 'Climate Tipping Point',
            probability: 0.0067, // 1:150 years
            impactRange: [0.2, 0.5],
            description: 'Irreversible climate change, extreme weather normalization',
            philippinesContext: 'Sea level rise, permanent weather disruption',
            enabled: false,
            severity: 'catastrophic'
        },
        {
            id: 'global_war',
            name: 'Global War',
            probability: 0.005, // 1:200 years
            impactRange: [0.0, 0.3],
            description: 'World War III, infrastructure targeting',
            philippinesContext: 'US-China conflict, regional warfare',
            enabled: false,
            severity: 'catastrophic'
        }
    ]
};

/**
 * Default configuration for extreme events modeling
 */
export const DEFAULT_EXTREME_EVENTS_CONFIG = {
    enabled: true,
    simulationMode: 'normal_only', // 'normal_only' | 'extreme_only' | 'hybrid'
    scenarios: 1000,
    correlationModeling: false,
    cascadingEffects: false,
    exportResults: true,
    confidenceIntervals: [0.90, 0.95, 0.99],
    riskCategories: {
        FINANCIAL_RISK: { enabled: true, weight: 1.0 },
        STRATEGIC_RISK: { enabled: true, weight: 1.0 },
        HAZARD_RISK: { enabled: true, weight: 1.0 },
        OPERATIONS_RISK: { enabled: true, weight: 1.0 },
        EXTREME_BLACK_SWAN: { enabled: false, weight: 0.5 }
    }
};

/**
 * External parameters for normal Monte Carlo simulation
 * These are the 32 market-driven variables with normal distributions
 */
export const EXTERNAL_PARAMETERS = {
    investmentPerMW: { mean: 500000, std: 50000, unit: 'USD/MW', description: 'Capital cost per megawatt installed' },
    annualHours: { mean: 1950, std: 80, unit: 'hours', description: 'Equivalent full-load hours per year' },
    onGridTariff: { mean: 0.198, std: 0.02, unit: 'USD/kWh', description: 'Electricity selling price' },
    pvDegradationRate: { mean: 0.005, std: 0.002, unit: '%/year', description: 'Annual efficiency decline of PV panels' },
    operatingCostRate: { mean: 0.08, std: 0.02, unit: '% of revenue', description: 'Operating cost as percentage of revenue' },
    operatingCostEscalationRate: { mean: 0.025, std: 0.005, unit: '%/year', description: 'Annual increase in operating costs' },
    longTermLoanRate: { mean: 0.075, std: 0.012, unit: '%/year', description: 'Interest rate on project debt' },
    landCostPerHectare: { mean: 6177, std: 1235, unit: 'USD/hectare/year', description: 'Annual land lease cost per hectare' },
    depreciationPeriod: { mean: 20, std: 2, unit: 'years', description: 'Asset depreciation timeline' },
    salvageValueRate: { mean: 0.08, std: 0.03, unit: '%', description: 'Residual asset value at end of life' },
    vatRate: { mean: 0.12, std: 0, unit: '%', description: 'Value Added Tax rate' },
    surtaxRate: { mean: 0.10, std: 0.05, unit: '%', description: 'Additional tax on VAT' },
    incomeTaxRate: { mean: 0.25, std: 0.05, unit: '%', description: 'Corporate income tax rate' },
    statutoryReserveRate: { mean: 0.10, std: 0.05, unit: '%', description: 'Required profit reserve percentage' },
    discountRate: { mean: 0.08, std: 0.015, unit: '%/year', description: 'Weighted Average Cost of Capital (WACC)' },
    // BESS-specific external parameters
    bessCostPerMWh: { mean: 100000, std: 10000, unit: 'USD/MWh', description: 'Battery energy storage cost per MWh' },
    bessPowerCostPerMW: { mean: 100000, std: 10000, unit: 'USD/MW', description: 'Power electronics cost per MW' },
    bessRoundTripEfficiency: { mean: 0.87, std: 0.015, unit: '%', description: 'Energy efficiency for charge-discharge cycle' },
    bessDegradationRate: { mean: 0.018, std: 0.005, unit: '%/year', description: 'Annual battery capacity degradation' },
    bessOperatingCostRate: { mean: 10000, std: 2500, unit: 'USD/MW/year', description: 'Annual BESS maintenance costs' },
    bessReplacementPeriod: { mean: 15, std: 2, unit: 'years', description: 'Battery replacement cycle' },
    bessReplacementCostFactor: { mean: 0.35, std: 0.1, unit: '% of original', description: 'Replacement cost relative to original investment' },
    bessArbitrageCycles: { mean: 1.2, std: 0.2, unit: 'cycles/day', description: 'Daily charge-discharge cycles for arbitrage' },
    bessArbitrageSpread: { mean: 0.35, std: 0.125, unit: '% of tariff', description: 'Price differential for energy arbitrage' },
    bessAncillaryRate: { mean: 28000, std: 5000, unit: 'USD/MW/year', description: 'Grid stability service compensation' },
    bessCapacityRate: { mean: 6000, std: 1000, unit: 'USD/MW/month', description: 'Capacity market payments' },
    bessDemandChargeReduction: { mean: 9500, std: 1500, unit: 'USD/MW/month', description: 'Peak demand reduction value' }
};

/**
 * Utility functions for extreme events configuration
 */

/**
 * Get all events by category
 * @param {string} categoryId - Risk category ID
 * @returns {Array} Array of events in the category
 */
export function getEventsByCategory(categoryId) {
    return EXTREME_EVENTS[categoryId] || [];
}

/**
 * Get all enabled events
 * @returns {Array} Array of all enabled events
 */
export function getEnabledEvents() {
    const enabledEvents = [];
    Object.keys(EXTREME_EVENTS).forEach(categoryId => {
        const categoryEvents = EXTREME_EVENTS[categoryId].filter(event => event.enabled);
        enabledEvents.push(...categoryEvents.map(event => ({
            ...event,
            category: categoryId,
            categoryName: RISK_CATEGORIES[categoryId].name
        })));
    });
    return enabledEvents;
}

/**
 * Get total number of events by category
 * @returns {Object} Event counts by category
 */
export function getEventCounts() {
    const counts = {};
    Object.keys(EXTREME_EVENTS).forEach(categoryId => {
        const total = EXTREME_EVENTS[categoryId].length;
        const enabled = EXTREME_EVENTS[categoryId].filter(event => event.enabled).length;
        counts[categoryId] = { total, enabled };
    });
    return counts;
}

/**
 * Calculate expected annual loss from extreme events
 * @returns {number} Expected annual loss as multiplier
 */
export function calculateExpectedAnnualLoss() {
    let totalExpectedLoss = 0;
    
    Object.keys(EXTREME_EVENTS).forEach(categoryId => {
        const category = RISK_CATEGORIES[categoryId];
        if (!category.enabled) return;
        
        const events = EXTREME_EVENTS[categoryId].filter(event => event.enabled);
        events.forEach(event => {
            const averageImpact = (event.impactRange[0] + event.impactRange[1]) / 2;
            const lossContribution = event.probability * (1 - averageImpact);
            totalExpectedLoss += lossContribution;
        });
    });
    
    return totalExpectedLoss;
}

/**
 * Get events by severity level
 * @param {string} severity - Severity level ('minor', 'moderate', 'severe', 'catastrophic')
 * @returns {Array} Array of events with specified severity
 */
export function getEventsBySeverity(severity) {
    const events = [];
    Object.keys(EXTREME_EVENTS).forEach(categoryId => {
        const categoryEvents = EXTREME_EVENTS[categoryId].filter(event => event.severity === severity);
        events.push(...categoryEvents.map(event => ({
            ...event,
            category: categoryId,
            categoryName: RISK_CATEGORIES[categoryId].name
        })));
    });
    return events;
}

/**
 * Validate extreme events configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export function validateConfiguration(config) {
    const errors = [];
    const warnings = [];
    
    // Check if at least one category is enabled
    const enabledCategories = Object.keys(config.riskCategories || {})
        .filter(catId => config.riskCategories[catId].enabled);
    
    if (enabledCategories.length === 0) {
        errors.push('At least one risk category must be enabled');
    }
    
    // Check scenario count
    if (!config.scenarios || config.scenarios < 100) {
        warnings.push('Scenario count should be at least 100 for reliable results');
    }
    
    if (config.scenarios > 10000) {
        warnings.push('High scenario count may impact performance');
    }
    
    // Check if extreme black swan events are enabled
    if (config.riskCategories?.EXTREME_BLACK_SWAN?.enabled) {
        warnings.push('Extreme black swan events have very low probability but massive impact');
    }
    
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}

export default {
    RISK_CATEGORIES,
    EXTREME_EVENTS,
    DEFAULT_EXTREME_EVENTS_CONFIG,
    EXTERNAL_PARAMETERS,
    getEventsByCategory,
    getEnabledEvents,
    getEventCounts,
    calculateExpectedAnnualLoss,
    getEventsBySeverity,
    validateConfiguration
};